/**
 * Prim İşlemleri — Saf Hesaplama Fonksiyonları
 *
 * Firestore, React, i18n veya Zustand bağımlılığı yoktur ve olamaz.
 * Tüketiciler: features/commissions, features/bills (taşındığında)
 */

/**
 * Tek bir hizmet için prim tutarını hesaplar.
 *
 * @param {{ serviceId: string, servicePrice: number, sessions: number }} service
 * @param {{ type: 'percentage'|'fixed', defaultRate: number, perServiceRates?: Object }} config
 * @returns {number}
 */
export function calculateCommissionForService(service, config) {
  if (!config || !config.defaultRate) return 0;
  const rate =
    config.perServiceRates?.[service.serviceId] ?? config.defaultRate;
  if (!rate || rate <= 0) return 0;
  const sessions = Number(service.sessions) || 1;
  if (config.type === 'percentage') {
    return Math.round(((service.servicePrice * sessions * rate) / 100) * 100) / 100;
  }
  // fixed: TL/seans
  return Math.round(rate * sessions * 100) / 100;
}

/**
 * Bill'deki tüm hizmetler için toplam primi hesaplar.
 *
 * @param {Array} services
 * @param {object|null} commissionConfig
 * @returns {number}
 */
export function calculateTotalCommission(services, commissionConfig) {
  if (!commissionConfig || !Array.isArray(services)) return 0;
  return services.reduce(
    (sum, svc) => sum + calculateCommissionForService(svc, commissionConfig),
    0
  );
}

/**
 * Firestore'a yazılmaya hazır `commission_records` objesi oluşturur.
 * createdAt alanı servis katmanında serverTimestamp() ile eklenir.
 *
 * @param {{ id: string, date: string, employeeId: string, services: Array }} bill
 * @param {{ type: string, defaultRate: number, perServiceRates?: Object }} commissionConfig
 * @param {string} businessId
 * @returns {object}
 */
export function buildCommissionRecord(bill, commissionConfig, businessId) {
  const period = bill.date.slice(0, 7); // YYYY-MM

  const services = (bill.services || []).map((svc) => {
    const rate =
      commissionConfig.perServiceRates?.[svc.id] ?? commissionConfig.defaultRate;
    const commissionAmount = calculateCommissionForService(
      { serviceId: svc.id, servicePrice: Number(svc.price) || 0, sessions: svc.sessions ?? 1 },
      commissionConfig
    );
    return {
      serviceId:        svc.id,
      serviceName:      svc.name,
      servicePrice:     Number(svc.price) || 0,
      sessions:         svc.sessions ?? 1,
      rate:             rate ?? 0,
      commissionAmount,
    };
  });

  const totalCommission = services.reduce((s, c) => s + c.commissionAmount, 0);

  return {
    businessId,
    employeeId:      bill.employeeId,
    billId:          bill.id,
    billDate:        bill.date,
    period,
    services,
    totalCommission,
    status:          'pending',
    paidAt:          null,
    paidBy:          null,
    note:            '',
  };
}

/**
 * Dönem seçici için YYYY-MM formatında geçerli ay döndürür.
 * @returns {string}
 */
export function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}
